/**
 * Location detection utilities for payment provider selection
 */

const LOCATION_CACHE_KEY = 'nkenne_detected_location';

/**
 * Get cached location data
 * @returns {Object|null} Cached location data or null
 */
const getCachedLocation = () => {
  try {
    debugger
    const cached = localStorage.getItem(LOCATION_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch (e) {
    console.warn('Failed to parse cached location:', e);
    return null;
  }
};

/**
 * Store location data in cache
 * @param {Object} data - Location data from API
 */
const setCachedLocation = (data) => {
  try {
    if (data) {
      localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(data));
    }
  } catch (e) {
    console.warn('Failed to cache location:', e);
  }
};

/**
 * Clear location cache (call on logout)
 */
export const clearLocationCache = () => {
  localStorage.removeItem(LOCATION_CACHE_KEY);
};

/**
 * Internal helper to fetch location from API with caching
 * @returns {Promise<Object|null>} Location data
 */
const fetchLocationData = async () => {
  // Check cache first
  const cached = getCachedLocation();
  if (cached) {
    return cached;
  }

  try {
    const response = await fetch('https://ipapi.co/json/');
    if (!response.ok) {
      throw new Error(`Location API error: ${response.status}`);
    }
    const data = await response.json();

    // Cache the successful response
    if (data && data.country_code) {
      setCachedLocation(data);
    }

    return data;
  } catch (error) {
    console.warn('IP geolocation API failed:', error);
    return null;
  }
};

// List of African country codes
const AFRICAN_COUNTRIES = [
  'DZ', 'AO', 'BW', 'BI', 'CM', 'CV', 'CF', 'TD', 'KM', 'CG', 'CD', 'CI', 'DJ', 'EG', 'GQ', 'ER', 'ET', 'GA', 'GM', 'GH', 'GN', 'GW', 'KE', 'LS', 'LR', 'LY', 'MG', 'MW', 'ML', 'MR', 'MU', 'MA', 'MZ', 'NA', 'NE', 'NG', 'RW', 'ST', 'SN', 'SC', 'SL', 'SO', 'ZA', 'SS', 'SD', 'SZ', 'TZ', 'TG', 'TN', 'UG', 'ZM', 'ZW'
];

// African timezones
const AFRICAN_TIMEZONES = [
  'Africa/Abidjan', 'Africa/Accra', 'Africa/Addis_Ababa', 'Africa/Algiers', 'Africa/Asmara', 'Africa/Bamako', 'Africa/Bangui', 'Africa/Banjul', 'Africa/Bissau', 'Africa/Blantyre', 'Africa/Brazzaville', 'Africa/Bujumbura', 'Africa/Cairo', 'Africa/Casablanca', 'Africa/Ceuta', 'Africa/Conakry', 'Africa/Dakar', 'Africa/Dar_es_Salaam', 'Africa/Djibouti', 'Africa/Douala', 'Africa/El_Aaiun', 'Africa/Freetown', 'Africa/Gaborone', 'Africa/Harare', 'Africa/Johannesburg', 'Africa/Juba', 'Africa/Kampala', 'Africa/Khartoum', 'Africa/Kigali', 'Africa/Kinshasa', 'Africa/Lagos', 'Africa/Libreville', 'Africa/Lome', 'Africa/Luanda', 'Africa/Lubumbashi', 'Africa/Lusaka', 'Africa/Malabo', 'Africa/Maputo', 'Africa/Maseru', 'Africa/Mbabane', 'Africa/Mogadishu', 'Africa/Monrovia', 'Africa/Nairobi', 'Africa/Ndjamena', 'Africa/Niamey', 'Africa/Nouakchott', 'Africa/Ouagadougou', 'Africa/Porto-Novo', 'Africa/Sao_Tome', 'Africa/Tripoli', 'Africa/Tunis', 'Africa/Windhoek'
];

/**
 * Detect if user is from Africa based on various methods
 * @returns {Promise<boolean>} True if user is from Africa
 */
export const isUserFromAfrica = async () => {
  try {
    // Method 1: Check user's timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (AFRICAN_TIMEZONES.includes(timezone)) {
      return true;
    }

    // Method 2: Check browser language for African locales
    const language = navigator.language || navigator.languages[0];
    const africanLocales = ['en-NG', 'ig-NG', 'yo-NG', 'en-KE', 'sw-KE', 'en-ZA', 'af-ZA', 'en-GH', 'en-UG', 'sw-TZ', 'en-TZ', 'ar-EG', 'ar-MA', 'ar-TN', 'ar-DZ', 'ar-LY', 'fr-CM', 'fr-SN', 'fr-CI', 'fr-ML', 'fr-BF', 'fr-NE', 'fr-TD', 'fr-MG', 'fr-RW', 'fr-BI', 'fr-DJ', 'fr-KM', 'fr-MU', 'fr-SC', 'pt-AO', 'pt-MZ', 'pt-CV', 'pt-ST', 'pt-GW', 'pt-GQ', 'es-GQ', 'so-SO', 'am-ET', 'ti-ER', 'rw-RW', 'rn-BI', 'sw-UG', 'lg-UG', 'zu-ZA', 'xh-ZA', 'st-ZA', 'tn-ZA', 'ss-ZA', 've-ZA', 'ts-ZA', 'nr-ZA', 'nso-ZA'];
    if (africanLocales.some(locale => language.includes(locale))) {
      return true;
    }

    // Method 3: Check IP geolocation (with caching)
    const data = await fetchLocationData();
    if (data && AFRICAN_COUNTRIES.includes(data.country_code)) {
      return true;
    }

    // Method 4: Check if user has African phone number format in localStorage
    const savedPhone = localStorage.getItem('userPhone');
    if (savedPhone) {
      // Common African country codes
      const africanPhoneCodes = ['+234', '234', '+254', '254', '+27', '27', '+233', '233', '+256', '256', '+255', '255', '+20', '20', '+212', '212', '+216', '216', '+213', '213', '+218', '218', '+237', '237', '+221', '221', '+225', '225', '+223', '223', '+226', '226', '+227', '227', '+228', '228', '+229', '229', '+230', '230', '+231', '231', '+232', '232', '+235', '235', '+236', '236', '+238', '238', '+239', '239', '+240', '240', '+241', '241', '+242', '242', '+243', '243', '+244', '244', '+245', '245', '+246', '246', '+248', '248', '+249', '249', '+250', '250', '+251', '251', '+252', '252', '+253', '253', '+257', '257', '+258', '258', '+260', '260', '+261', '261', '+262', '262', '+263', '263', '+264', '264', '+265', '265', '+266', '266', '+267', '267', '+268', '268', '+269', '269', '+290', '290', '+291', '291', '+297', '297', '+298', '298'];
      if (africanPhoneCodes.some(code => savedPhone.startsWith(code))) {
        return true;
      }
    }

    // Method 5: Check if user has explicitly set their location to an African country
    const savedLocation = localStorage.getItem('userLocation');
    if (savedLocation && AFRICAN_COUNTRIES.includes(savedLocation)) {
      return true;
    }

    return false;
  } catch (error) {
    console.warn('Location detection failed:', error);
    return false;
  }
};

/**
 * Get user's country code
 * @returns {Promise<string>} Country code (e.g., 'NG', 'US', 'GB')
 */
export const getUserCountryCode = async () => {
  try {
    // Try IP geolocation first (with caching)
    const data = await fetchLocationData();
    if (data && data.country_code) {
      return data.country_code;
    }

    return 'US'; // Default if API failed and no cache
  } catch (error) {
    console.warn('IP geolocation failed:', error);

    // Fallback to timezone detection
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (AFRICAN_TIMEZONES.includes(timezone)) {
      // Map timezone to country code
      const timezoneToCountry = {
        'Africa/Lagos': 'NG',
        'Africa/Nairobi': 'KE',
        'Africa/Johannesburg': 'ZA',
        'Africa/Accra': 'GH',
        'Africa/Kampala': 'UG',
        'Africa/Dar_es_Salaam': 'TZ',
        'Africa/Cairo': 'EG',
        'Africa/Casablanca': 'MA',
        'Africa/Tunis': 'TN',
        'Africa/Algiers': 'DZ',
        'Africa/Tripoli': 'LY'
      };
      return timezoneToCountry[timezone] || 'NG'; // Default to Nigeria if timezone not mapped
    }

    return 'US'; // Default fallback
  }
};

/**
 * Set user's preferred location manually
 * @param {string} countryCode - Country code (e.g., 'NG', 'US')
 */
export const setUserLocation = (countryCode) => {
  localStorage.setItem('userLocation', countryCode);
};

/**
 * Get user's preferred payment provider based on location
 * @returns {Promise<'paystack'|'stripe'>} Payment provider to use (Paystack only for Nigeria)
 */
export const getPreferredPaymentProvider = async () => {
  const isNigeria = await isUserFromNigeria();
  return isNigeria ? 'paystack' : 'stripe';
};

/**
 * Check if user is from Nigeria
 * @returns {Promise<boolean>} True if user is from Nigeria
 */
export const isUserFromNigeria = async () => {
  try {
    // Method 1: Check user's timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone === 'Africa/Lagos') {
      return true;
    }

    // Method 2: Check browser language for Nigerian locales
    const language = navigator.language || navigator.languages[0];
    const nigerianLocales = ['en-NG', 'ig-NG', 'yo-NG', 'ha-NG'];
    if (nigerianLocales.some(locale => language.includes(locale))) {
      return true;
    }

    // Method 3: Check IP geolocation (with caching)
    const data = await fetchLocationData();
    if (data && data.country_code === 'NG') {
      return true;
    }

    // Method 4: Check if user has Nigerian phone number format in localStorage
    const savedPhone = localStorage.getItem('userPhone');
    if (savedPhone) {
      // Nigerian country codes
      const nigerianPhoneCodes = ['+234', '234'];
      if (nigerianPhoneCodes.some(code => savedPhone.startsWith(code))) {
        return true;
      }
    }

    // Method 5: Check if user has explicitly set their location to Nigeria
    const savedLocation = localStorage.getItem('userLocation');
    if (savedLocation === 'NG') {
      return true;
    }

    return false;
  } catch (error) {
    console.warn('Location detection failed:', error);
    return false;
  }
};

/**
 * Check if Paystack is available for the user's location
 * @returns {Promise<boolean>} True if Paystack should be used (only for Nigeria)
 */
export const shouldUsePaystack = async () => {
  return await isUserFromNigeria();
};

/**
 * Get user's region for display purposes
 * @returns {Promise<string>} Region name (e.g., 'Africa', 'United States')
 */
export const getUserRegion = async () => {
  const isAfrica = await isUserFromAfrica();
  if (isAfrica) {
    const countryCode = await getUserCountryCode();
    const countryNames = {
      'NG': 'Nigeria', 'KE': 'Kenya', 'ZA': 'South Africa', 'GH': 'Ghana', 'UG': 'Uganda', 'TZ': 'Tanzania', 'EG': 'Egypt', 'MA': 'Morocco', 'TN': 'Tunisia', 'DZ': 'Algeria', 'LY': 'Libya', 'ET': 'Ethiopia', 'CI': 'Ivory Coast', 'SN': 'Senegal', 'ML': 'Mali', 'BF': 'Burkina Faso', 'NE': 'Niger', 'TD': 'Chad', 'CM': 'Cameroon', 'CF': 'Central African Republic', 'CG': 'Republic of the Congo', 'CD': 'Democratic Republic of the Congo', 'AO': 'Angola', 'ZM': 'Zambia', 'ZW': 'Zimbabwe', 'BW': 'Botswana', 'NA': 'Namibia', 'SZ': 'Eswatini', 'LS': 'Lesotho', 'MW': 'Malawi', 'MZ': 'Mozambique', 'MG': 'Madagascar', 'MU': 'Mauritius', 'SC': 'Seychelles', 'KM': 'Comoros', 'DJ': 'Djibouti', 'SO': 'Somalia', 'ER': 'Eritrea', 'SS': 'South Sudan', 'SD': 'Sudan', 'LR': 'Liberia', 'SL': 'Sierra Leone', 'GN': 'Guinea', 'GW': 'Guinea-Bissau', 'GM': 'Gambia', 'CV': 'Cape Verde', 'ST': 'São Tomé and Príncipe', 'GQ': 'Equatorial Guinea', 'GA': 'Gabon', 'RW': 'Rwanda', 'BI': 'Burundi', 'MR': 'Mauritania'
    };
    return countryNames[countryCode] || 'Africa';
  }
  return 'United States';
};

